import { openDB, type DBSchema, type IDBPDatabase } from "idb";

import type { CurrentUser } from "@/context/AuthContext";
import type { SnimljenKontekst } from "@/lib/kontekst";
import type { SnimljeniPoslani } from "@/lib/poslano";
import type { StavkaReda } from "@/lib/red";

/**
 * The device database — one file for everything the phone has to survive
 * being closed with (ADR-0004 §2.8).
 *
 * Four stores, and they are different in kind. `red` is work that exists
 * nowhere else and may never be dropped; `kontekst`, `poslano` and `korisnik`
 * are copies of what the server already holds and may be thrown away at any
 * time. Keeping them in one database is what lets a version upgrade see all of
 * them at once — separate databases upgrading independently is how one ends up
 * half migrated.
 */

export interface Shema extends DBSchema {
  red: {
    key: string;
    value: StavkaReda;
    indexes: { "po-stanju": string };
  };
  kontekst: {
    key: string;
    value: SnimljenKontekst;
  };
  poslano: {
    key: string;
    value: SnimljeniPoslani;
  };
  korisnik: {
    key: string;
    value: CurrentUser;
  };
}

const IME = "avm-sati";
/**
 * 1: the queue. 2: the cached roster the entry screen reads offline, and the
 * signed-in identity that lets it be shown at all after a restart (§2.7).
 * 3: what the server already accepted for a day, so a screen with no signal
 * stops offering hours that are already recorded (O-19).
 */
const VERZIJA = 3;

let bazaPromise: Promise<IDBPDatabase<Shema>> | null = null;

export function baza(): Promise<IDBPDatabase<Shema>> {
  bazaPromise ??= openDB<Shema>(IME, VERZIJA, {
    upgrade(db) {
      // Each store is created only if missing rather than in a `switch` on the
      // old version: a phone can arrive here from version 0 or from version 1,
      // and the branch that only handles one of those is the branch that
      // throws on somebody's device and takes the queue with it.
      if (!db.objectStoreNames.contains("red")) {
        const red = db.createObjectStore("red", {
          keyPath: "zapis.client_uuid",
        });
        red.createIndex("po-stanju", "stanje");
      }
      if (!db.objectStoreNames.contains("kontekst")) {
        db.createObjectStore("kontekst");
      }
      if (!db.objectStoreNames.contains("poslano")) {
        db.createObjectStore("poslano");
      }
      if (!db.objectStoreNames.contains("korisnik")) {
        db.createObjectStore("korisnik");
      }
    },
  });
  return bazaPromise;
}

/**
 * Close the connection and drop the cached handle.
 *
 * Closing matters and forgetting alone does not: an open connection blocks
 * `deleteDB` and a version upgrade indefinitely, and "indefinitely" here means
 * a promise that never settles rather than an error anybody can see.
 */
export async function zatvoriBazu(): Promise<void> {
  const otvorena = bazaPromise;
  bazaPromise = null;
  if (otvorena) {
    (await otvorena).close();
  }
}
